// SCALABILITY RULE — SOURCE DRAWER SYSTEM
// ─────────────────────────────────────────
// This is the ONE drawer for source details across the entire app.
// Do not create other source drawers. Extend this one.
// Future source types (podcasts, books, academic papers): add here.

import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, FileText, User, MapPin, BookOpen, X } from "lucide-react";
import type { SourcePayload, SourceData } from "@/hooks/useSourceDrawer";

interface SourceDrawerProps {
  open: boolean;
  onClose: () => void;
  payload: SourcePayload | null;
  sourceData: SourceData | null;
  loading: boolean;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  article: "Article",
  video: "Vidéo",
  pdf: "PDF",
  book: "Livre",
  podcast: "Podcast",
  study: "Étude",
  meta_analysis: "Méta-analyse",
  review: "Revue",
};

export function SourceDrawer({ open, onClose, payload, sourceData, loading }: SourceDrawerProps) {
  if (!payload) return null;

  const title = sourceData?.title || payload.title || "Source";
  const authors = sourceData?.authors || payload.authors;
  const url = sourceData?.url || payload.externalUrl;
  const citationLocator = payload.citationLocator;
  const sourceType = sourceData?.source_type;
  const isContextOnly = !payload.sourceId && !payload.externalUrl;

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="max-h-[85dvh]">
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Détail de la source
          </DrawerTitle>
        </DrawerHeader>

        <div className="overflow-y-auto px-4 pb-6 space-y-4">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : isContextOnly ? (
            /* Context-only mode (e.g. CerveauTipCard) */
            <div className="space-y-3">
              <p className="text-sm text-foreground leading-relaxed">
                Ce conseil est tiré de la bibliothèque scientifique PERFORM.
              </p>
              <p className="text-xs text-muted-foreground">
                Explore la bibliothèque pour découvrir les milliers de faits scientifiques validés.
              </p>
            </div>
          ) : (
            /* Full source display */
            <div className="space-y-4">
              {/* Title */}
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-base font-medium text-foreground leading-relaxed">
                  {title}
                </p>
              </div>

              {/* Source type badge */}
              {sourceType && (
                <Badge variant="outline" className="text-xs border-border">
                  {SOURCE_TYPE_LABELS[sourceType] || sourceType}
                </Badge>
              )}

              {/* Authors */}
              {authors && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-3.5 w-3.5 shrink-0" />
                  <span>{authors}</span>
                </div>
              )}

              {/* Citation locator */}
              {citationLocator && (
                <div className="flex items-center gap-2 text-xs text-primary">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span>{citationLocator}</span>
                </div>
              )}

              {/* External URL */}
              {url ? (
                <Button
                  variant="outline"
                  className="w-full gap-2 min-h-[48px] border-primary/30 text-primary"
                  onClick={() => window.open(url, "_blank", "noopener")}
                >
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir la source
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  Source sans lien externe disponible
                </p>
              )}
            </div>
          )}

          {/* Close button */}
          <Button
            variant="ghost"
            className="w-full gap-2 min-h-[48px] text-muted-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
            Fermer
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
